<template>
    <div class="flex flex-col gap-3">
        <!-- Parent Nested Select -->
        <div class="flex flex-col gap-1">
            <label class="font-semibold" :for="name">{{ label }}<span v-if="required" class="text-red-600">*</span></label>
            <Multiselect 
                v-model="parentInputValue" 
                :options="transformedArray" 
                :multiple="false" 
                @select="fetchChildren"
                @clear="clearChildren"
            />
            <p v-show="parentErrorMessage || parentMeta.valid">
                {{ parentErrorMessage }}
            </p>
        </div>

        <!-- Child Multiselect with Tags -->
        <div v-if="childrenOptions.length > 0" class="flex flex-col gap-1">
            <label class="font-semibold" :for="'child'">{{ childLabel }}</label>
            <Multiselect 
                v-model="selectedChildren" 
                :options="childrenOptions" 
                mode="tags" 
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from "vue-i18n";
const { t } = useI18n();
import { useField } from 'vee-validate';
import * as yup from 'yup';
import Multiselect from '@vueform/multiselect';

const props = defineProps({
    value: {
        type: String,
        default: undefined,
    },
    label: {
        type: String,
        required: true,
    },
    name: {
        type: String,
        required: true,
    },
    required: {
        type: Boolean,
        default: true,
    },
    options: {
        type: Array,
        required: true,
    },
    property: {
        type: Number,
        default: undefined,
    },
    defaultParentValue: {
        type: String,
        default: undefined,
    },
    defaultChildValues: {
        type: Array,
        default: () => [],
    },
});

// Validation rules
let rules = yup.string().required(t('warning.REQUIRED_MESSAGE'));
const { meta: parentMeta, value: parentInputValue, errorMessage: parentErrorMessage, validate: parentValidate } = useField(props.name, rules, {
    initialValue: props.value,
});

// Reactive child options and selections
let childrenOptions = ref([]);
let selectedChildren = ref([]);
let childLabel = ref('');

// Transform options for the parent field
const transformedArray = computed(() => {
    return props.options?.map(item => ({
        value: item.id,
        label: item.name,
    }));
});

// Fetch child options when a parent is selected
const fetchChildren = () => {
    const selectedParent = props.options?.find(option => option.id === parentInputValue.value);
    if (selectedParent) {
        childrenOptions.value = selectedParent.children.map(child => ({
            value: child.id,
            label: child.name,
        }));
        childLabel.value = selectedParent.name; // Dynamic child label
        selectedChildren.value = props.defaultChildValues; // Set default child values
    } else {
        clearChildren();
    }
};

// Clear child options and selections
const clearChildren = () => {
    childrenOptions.value = [];
    selectedChildren.value = [];
    childLabel.value = '';
};

// Initialize default parent and child values
const initializeDefaults = () => {
    if (props.defaultParentValue) {
        parentInputValue.value = props.defaultParentValue;
        fetchChildren();
    }
};

// Watch for changes in options and initialize values
watch(() => props.options, (newOptions) => {
    if (newOptions) {
        initializeDefaults();
    }
}, { immediate: true });

// Expose validation method
const validateField = async () => {
    const parentValidatedData = await parentValidate();
    if (parentInputValue.value && selectedChildren.value) {
        return { value: [parentInputValue.value, ...selectedChildren.value], valid: parentValidatedData.valid, mode: 'multi', property: props.property };
    }
    return { value: undefined, valid: false, mode: 'multi' };
};

defineExpose({
    validateField,
});
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
